import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { Edit, Info, Percent, ChevronDown, ChevronUp } from "lucide-react";
import ROSMAEditDialog from "./ROSMAEditDialog";
const formatCurrency = (value, fractionDigits = 2) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
}).format(value ?? 0);
const formatRosma = (value) => `${(value ?? 0).toFixed(2)}x`;
const normalizeInvestment = (value) => Number(value) || 0;
const ROSMACalculation = ({ data, savingsSummary }) => {
    const rosmaMetrics = data.rosma_metrics;
    const totalSavings = data.total_savings;
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [investment, setInvestment] = useState(normalizeInvestment(rosmaMetrics?.total_investment_cost ?? 0));
    const [isCalculationOpen, setIsCalculationOpen] = useState(false);
    useEffect(() => {
        if (rosmaMetrics?.total_investment_cost !== undefined) {
            setInvestment(normalizeInvestment(rosmaMetrics.total_investment_cost));
        }
    }, [rosmaMetrics]);
    const efficiency = savingsSummary?.efficiency ?? totalSavings?.total_efficiency ?? 0;
    const effectivenessMin = savingsSummary?.effectivenessMin ?? totalSavings?.min_effectiveness ?? 0;
    const effectivenessMax = savingsSummary?.effectivenessMax ?? totalSavings?.max_effectiveness ?? 0;
    const annualSavingsMin = useMemo(() => Math.min(efficiency, effectivenessMin), [efficiency, effectivenessMin]);
    const annualSavingsMax = useMemo(() => Math.max(efficiency, effectivenessMax), [efficiency, effectivenessMax]);
    const computedMinRosma = investment ? annualSavingsMin / investment : 0;
    const computedMaxRosma = investment ? annualSavingsMax / investment : 0;
    const rosmaMin = rosmaMetrics?.min_rosma ?? computedMinRosma;
    const rosmaMax = rosmaMetrics?.max_rosma ?? computedMaxRosma;
    return (<>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Percent className="h-5 w-5 text-primary"/>
              <CardTitle>ROSMA (Return on Supply Management Assets)</CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
              <Edit className="h-4 w-4 mr-2"/>
              Edit Investment
            </Button>
          </div>
          <CardDescription>Return on investment calculation for AI implementation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-center justify-center p-8 bg-gradient-to-br from-primary/15 to-primary/5 rounded-lg border border-primary/30 shadow-md">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Expected ROSMA Range</p>
                <p className="text-4xl font-bold text-primary">
                  {formatRosma(rosmaMin)} - {formatRosma(rosmaMax)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Reacts instantly to edited savings and investment assumptions
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 border border-border/60 rounded-lg bg-card/90 shadow-sm">
                <p className="text-sm text-muted-foreground mb-1">Total Investment (USD)</p>
                <p className="text-2xl font-bold">{formatCurrency(investment)}</p>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <p>- AI platform &amp; tools</p>
                  <p>- Implementation costs</p>
                  <p>- Training &amp; change management</p>
                  <p>- Infrastructure setup &amp; enablement</p>
                </div>
              </div>

              <div className="p-4 border border-border/60 rounded-lg bg-card/90 shadow-sm">
                <p className="text-sm text-muted-foreground mb-1">Annual Savings (Min)</p>
                <p className="text-2xl font-bold text-success">{formatCurrency(annualSavingsMin)}</p>
                <div className="mt-3 text-xs text-muted-foreground">
                  <p>Conservative estimate derived from efficiency vs. effectiveness comparison</p>
                </div>
              </div>

              <div className="p-4 border border-border/60 rounded-lg bg-card/90 shadow-sm">
                <p className="text-sm text-muted-foreground mb-1">Annual Savings (Max)</p>
                <p className="text-2xl font-bold text-success">{formatCurrency(annualSavingsMax)}</p>
                <div className="mt-3 text-xs text-muted-foreground">
                  <p>Optimistic estimate considering the higher savings scenario</p>
                </div>
              </div>
            </div>

            <Collapsible open={isCalculationOpen} onOpenChange={setIsCalculationOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-between p-4 hover:bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary"/>
                    <span className="font-semibold">Detailed Calculation Methodology</span>
                  </div>
                  {isCalculationOpen ? (<ChevronUp className="h-4 w-4 text-muted-foreground"/>) : (<ChevronDown className="h-4 w-4 text-muted-foreground"/>)}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-muted/50 border border-muted/70 text-muted-foreground">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Info className="h-4 w-4"/>
                        ROSMA Formula
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      <div className="font-mono bg-background p-3 rounded border text-xs text-foreground">
                        ROSMA = Total Annual Savings / Total Investment
                      </div>
                      <div className="space-y-1">
                        <div>
                          Min ROSMA = {formatCurrency(annualSavingsMin)} / {formatCurrency(investment)} ={" "}
                          <span className="font-medium text-foreground">{formatRosma(computedMinRosma)}</span>
                        </div>
                        <div>
                          Max ROSMA = {formatCurrency(annualSavingsMax)} / {formatCurrency(investment)} ={" "}
                          <span className="font-medium text-foreground">{formatRosma(computedMaxRosma)}</span>
                        </div>
                        {(rosmaMetrics?.min_rosma || rosmaMetrics?.max_rosma) && (<p className="text-xs text-muted-foreground pt-1">
                            Dashboard provided range: {formatRosma(rosmaMin)} - {formatRosma(rosmaMax)}
                          </p>)}
                      </div>
                      <p className="pt-2 border-t">
                        Measures the return on investment specifically for supply management technology and process improvements.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-muted/50 border border-muted/70 text-muted-foreground">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Info className="h-4 w-4"/>
                        Investment Considerations
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                      <p>Allocate investments across the following areas to achieve the projected ROSMA:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>AI platform licenses &amp; cognitive services</li>
                        <li>Implementation squads and integration workstreams</li>
                        <li>Data engineering, enablement, and quality initiatives</li>
                        <li>Training, change management, and operating model design</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </CardContent>
      </Card>

      <ROSMAEditDialog open={showEditDialog} onOpenChange={setShowEditDialog} investment={investment} onSave={setInvestment}/>
    </>);
};
export default ROSMACalculation;
